// PR-1380 — menu "Scellé" (sous-menus par type, vocabulaire lu en API, pas
// en dur) + emplacement des entrées manuelles carte (onglet "Cartes"). Les
// entrées viennent de l'extension (PR-1377, "+" sur le panel de la modale).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Package, ExternalLink } from "lucide-react";
import { Card, CardBody } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Empty } from "@/components/ui/empty";
import { SkeletonRows } from "@/components/ui/skeleton";
import { api, type ManualEntry, type ManualEntryProductType } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/cn";

type Kind = "sealed" | "card";

export function ManualEntries() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const kind: Kind = params.get("kind") === "card" ? "card" : "sealed";
  const type = params.get("type") || "";

  const [types, setTypes] = useState<ManualEntryProductType[]>([]);
  const [items, setItems] = useState<ManualEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api<{ types: ManualEntryProductType[] }>("/api/manual-entries/types")
      .then((r) => setTypes(r.types))
      .catch(() => setTypes([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const u = new URL("/api/manual-entries", window.location.origin);
      u.searchParams.set("kind", kind);
      if (kind === "sealed" && type) u.searchParams.set("product_type", type);
      u.searchParams.set("limit", "100");
      const r = await api<{ items: ManualEntry[] }>(u.pathname + u.search);
      setItems(r.items);
    } catch (e) {
      setErr((e as Error).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [kind, type]);

  useEffect(() => {
    load();
  }, [load]);

  function setKind(k: Kind) {
    const next = new URLSearchParams(params);
    next.set("kind", k);
    next.delete("type");
    setParams(next, { replace: true });
  }
  function setType(code: string) {
    const next = new URLSearchParams(params);
    next.set("kind", "sealed");
    if (code) next.set("type", code);
    else next.delete("type");
    setParams(next, { replace: true });
  }

  const countByType = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      if (!it.product_type) continue;
      m.set(it.product_type, (m.get(it.product_type) || 0) + 1);
    }
    return m;
  }, [items]);

  return (
    <div className="space-y-6">
      <header>
        <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-fg">
          <Package className="h-3.5 w-3.5" />
          {t("manual.kicker") || "Entrées manuelles"}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("manual.title") || "Scellé & cartes (saisie manuelle)"}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-fg">
          {t("manual.subtitle") ||
            "Ajoutées depuis l'extension Chrome TCGPricer (bouton + sur la fiche produit)."}
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
        <KindTab active={kind === "sealed"} onClick={() => setKind("sealed")}>
          {t("manual.kind.sealed") || "Scellé"}
        </KindTab>
        <KindTab active={kind === "card"} onClick={() => setKind("card")}>
          {t("manual.kind.card") || "Cartes"}
        </KindTab>
      </div>

      {kind === "sealed" && (
        <div className="flex flex-wrap gap-2">
          <TypeTab active={!type} onClick={() => setType("")}>
            {t("manual.type.all") || "Tous"}
          </TypeTab>
          {types.map((tp) => (
            <TypeTab key={tp.code} active={type === tp.code} onClick={() => setType(tp.code)}>
              {tp.label_fr}
              {countByType.has(tp.code) && !type ? (
                <span className="ml-1 text-muted-fg">({countByType.get(tp.code)})</span>
              ) : null}
            </TypeTab>
          ))}
        </div>
      )}

      {err ? <p className="text-xs text-danger">{err}</p> : null}

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-lg bg-surface-2" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Empty
          title={t("manual.empty.title") || "Aucune entrée"}
          description={
            kind === "sealed" && type
              ? t("manual.empty.type") || "Aucune entrée pour ce type de scellé."
              : t("manual.empty.desc") || "Aucune entrée manuelle pour l'instant."
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {items.map((it) => (
            <EntryCard key={it.id} entry={it} />
          ))}
        </div>
      )}
    </div>
  );
}

function KindTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
        active ? "bg-primary text-primary-fg" : "text-muted-fg hover:bg-surface-2"
      )}
    >
      {children}
    </button>
  );
}
function TypeTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-fg hover:bg-surface-2"
      )}
    >
      {children}
    </button>
  );
}

function fmtPrice(v: ManualEntry["price"], cur?: string | null) {
  if (v == null) return null;
  return `${Number(v).toFixed(2)} ${cur || "EUR"}`;
}

function EntryCard({ entry }: { entry: ManualEntry }) {
  const price = fmtPrice(entry.price, entry.currency);
  const sub = [entry.extension_name, entry.number ? `n°${entry.number}` : null, entry.language]
    .filter(Boolean)
    .join(" · ");
  return (
    <Link to={`/manual-entries/${entry.id}`} className="block">
      <Card className="h-full overflow-hidden transition-shadow hover:shadow-soft">
        <div className="aspect-[3/4] w-full overflow-hidden bg-surface-2">
          <img
            src={`/api/manual-entries/${entry.id}/image`}
            alt={entry.name}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        </div>
        <CardBody className="space-y-1 p-3">
          <div className="truncate text-sm font-semibold" title={entry.name}>{entry.name}</div>
          {sub ? <div className="truncate text-xs text-muted-fg">{sub}</div> : null}
          <div className="flex items-center justify-between pt-1">
            {price ? <span className="text-sm font-bold text-success">{price}</span> : <span className="text-xs text-muted-fg">—</span>}
            {entry.condition ? <Badge tone="neutral" variant="soft" className="text-[10px]">{entry.condition}</Badge> : null}
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

export function ManualEntryDetail() {
  return <ManualEntryDetailInner />;
}

function ManualEntryDetailInner() {
  const { t } = useI18n();
  const id = window.location.pathname.split("/").pop();
  const [entry, setEntry] = useState<ManualEntry | null>(null);
  const [types, setTypes] = useState<ManualEntryProductType[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api<ManualEntry>(`/api/manual-entries/${id}`)
      .then(setEntry)
      .catch((e) => setErr((e as Error).message));
    api<{ types: ManualEntryProductType[] }>("/api/manual-entries/types")
      .then((r) => setTypes(r.types))
      .catch(() => setTypes([]));
  }, [id]);

  if (err) return <Empty title={t("manual.detail.notfound") || "Entrée introuvable"} description={err} />;
  if (!entry) return <SkeletonRows rows={1} cols={1} />;

  const price = fmtPrice(entry.price, entry.currency);
  const productTypeLabel = types.find((tp) => tp.code === entry.product_type)?.label_fr || entry.product_type;
  const rows: [string, string | null | undefined][] = [
    [t("manual.detail.kind") || "Type", entry.kind === "sealed" ? (t("manual.kind.sealed") || "Scellé") : (t("manual.kind.card") || "Carte")],
    [t("manual.detail.product_type") || "Type de produit", productTypeLabel],
    [t("manual.detail.extension") || "Édition", entry.extension_name],
    [t("manual.detail.number") || "Numéro", entry.number],
    [t("manual.detail.language") || "Langue", entry.language],
    [t("manual.detail.condition") || "État", entry.condition],
    [t("manual.detail.price") || "Prix (saisie manuelle)", price],
    [t("manual.detail.created_at") || "Ajoutée le", new Date(entry.created_at).toLocaleString()],
  ];

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link to={`/manual-entries?kind=${entry.kind}`} className="text-xs text-primary hover:underline">
        ← {t("manual.detail.back") || "Retour à la liste"}
      </Link>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="overflow-hidden rounded-lg bg-surface-2">
          <img src={`/api/manual-entries/${entry.id}/image`} alt={entry.name} className="w-full object-contain" />
        </div>
        <Card>
          <CardBody className="space-y-3 p-5">
            <h1 className="text-lg font-semibold">{entry.name}</h1>
            <dl className="space-y-1.5 text-sm">
              {rows
                .filter(([, v]) => v)
                .map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <dt className="text-muted-fg">{label}</dt>
                    <dd className="text-right font-medium">{value}</dd>
                  </div>
                ))}
            </dl>
            {entry.source_url ? (
              <a
                href={entry.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {t("manual.detail.source") || "Page source"} <ExternalLink className="h-3 w-3" />
              </a>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
